<template>
  <div :style="fill ? 'position: relative; height: 100%;' : 'position: relative; min-height: 400px;'">
    <div v-if="isRendering" class="chart-loading">
      <i class="tim-icons icon-refresh-01 chart-spin"></i>
    </div>
    <canvas ref="lineChart" :class="{ 'chart-hidden': isRendering, 'chart-canvas-fill': fill }"></canvas>
  </div>
</template>

<script>
import Chart from 'chart.js';
import moment from 'moment'
import {date} from "@/plugins/dates";
import _ from "lodash";
import currencies from "@/assets/currencies.json";

export default {
  name: 'LineChart',
  props: {
    chartData: {
      type: Array,
      required: true,
    },
    comparisonSeries: {
      type: Array,
      default: () => [],
      // each entry: { label: String, data: Array of {label, value} }
    },
    primaryLabel: {
      type: String,
      default: ''
    },
    granularity: {
      type: String,
      default: 'day'
    },
    // Fill the parent's height instead of the fixed 400px (dashboard tiles pass this so the
    // chart scales with the widget surface; other consumers keep the fixed height).
    fill: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      chart: null,
      isRendering: true,
      renderQueued: false,
      lastRenderSig: null,
      data_format: 'id',
      currency_symbol: '$',
      currency: null,
      currency_format: null,
      number_format: null,
      currencies: currencies.currencies
    };
  },
  computed: {
    labels() {
      return this.chartData.map((item, index) =>
        // index % 5 === 0 ? this.formatDate(item.label) : '' // Show only every 5th label
        this.formatDate(item.label)
      );
    },
    values() {
      return this.chartData.map(item => Number(item.value));
    },
    gridLineColor() {
      return localStorage.getItem('mode') !== '1' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)';
    },
    axisTextColor() {
      return localStorage.getItem('mode') === '0' ? '#000000' : '#ffffff'
    }
  },
  created() {
    this.initializeCurrency();
  },
  mounted() {
    this.scheduleCreateChart();
    if (this.fill) {
      window.addEventListener('dashboard-widget-resize', this.onTileResize);
    }
  },
  methods: {
    onTileResize() {
      if (this.chart) {
        this.chart.resize();
      }
    },
    // Data and comparison props update back to back, and the parent passes
    // comparison-series via a method call, so every parent re-render hands us
    // a NEW (but identical) array that trips the deep watcher. Coalesce to one
    // render per tick and skip renders whose content matches what is already
    // drawn; otherwise the chart re-animates from zero on every parent flush.
    scheduleCreateChart() {
      if (this.renderQueued) return;
      this.renderQueued = true;
      this.$nextTick(() => {
        this.renderQueued = false;
        const sig = JSON.stringify([this.chartData, this.comparisonSeries, this.primaryLabel, this.granularity]);
        if (this.chart && sig === this.lastRenderSig) return;
        this.isRendering = true;
        this.createChart();
        this.lastRenderSig = sig;
        this.isRendering = false;
      });
    },
    initializeCurrency() {
      try {
        const group = JSON.parse(localStorage.getItem('group'));
        this.currency = group?.currency || 'USD';
        this.currency_format = group?.currency_format || 0;
        this.number_format = group?.number_format || 0;

        const currencyData = _.find(this.currencies, { code: this.currency });
        this.currency_symbol = currencyData?.symbol || '$';
      } catch (error) {
        console.warn('Error loading currency settings:', error);
        // Fallback values
        this.currency = 'USD';
        this.currency_format = 0;
        this.number_format = 0;
        this.currency_symbol = '$';
      }
    },

    formatCurrency(amount) {
      if (amount == null || amount === '') return '';

      const value = parseFloat(amount);
      if (isNaN(value)) return amount;

      // Handle SEK and DKK formatting (postfix krone symbols)
      if (this.currency === 'SEK' || this.currency === 'DKK') {
        const formattedAmount = value.toLocaleString(this.currency === 'DKK' ? 'da-DK' : 'sv-SE', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        });
        return `${formattedAmount} ${this.currency === 'DKK' ? 'kr.' : 'kr'}`;
      }

      // Handle other currency formatting based on existing logic
      if (this.number_format === 1) {
        const localizedAmount = value.toLocaleString(undefined, {
          minimumFractionDigits: 2
        }).replace('.', ',');

        if (this.currency_format === 0) {
          return this.currency_symbol + localizedAmount;
        } else {
          return localizedAmount + ' ' + this.currency;
        }
      }

      // Default formatting
      if (this.currency_format === 0) {
        return this.currency_symbol + value.toFixed(2);
      } else {
        return value.toFixed(2) + ' ' + this.currency;
      }
    },

    getValueFormat(data_format, integer) {
      if (integer == null) {
        return '';
      }

      if (data_format === 'currency') {
        return this.formatCurrency(integer);
      }

      if (data_format === 'date') {
        return date(integer, this.date_format);
      }

      if (data_format === 'percent') {
        return Number(integer) + '%';
      }

      if (data_format === 'hours') {
        return Number(integer) + 'h';
      }

      return Number(integer);
    },

    formatDate(dateString) {
      if (this.granularity === 'month') {
        return moment(dateString, 'YYYY-MM-DD').format('MMM YYYY')
      }
      return moment(dateString, 'YYYY-MM-DD').format('MMM DD')
    },

    createChart() {
      if (this.chart) {
        this.chart.destroy();
      }

      // Set data format from first item if available
      this.data_format = this.chartData.length > 0 ? this.chartData[0].data_type : 'id';

      const canvas = this.$refs.lineChart;
      if (!this.fill) { canvas.height = 400; }

      const ctx = canvas.getContext('2d');
      // Comparison overlays align by index (day 1 to day 1); shorter periods
      // simply end early against the primary series' labels.
      const comparisonColors = ['#e14eca', '#ff8d72', '#00f2c3', '#fd5d93', '#c5fb70', '#3aeae1', '#988ef5', '#d7aa41', '#5f75d0'];
      const datasets = [
        {
          label: this.comparisonSeries.length > 0 ? this.primaryLabel : '',
          data: this.values,
          backgroundColor: 'rgba(0, 0, 0, 0)', // Transparent fill
          borderColor: '#1d8cf8', // Fixed line color (blue)
          pointBackgroundColor: '#f4f5f7', // Light color for points
          borderWidth: 2,
          pointRadius: 5,
          pointHoverRadius: 7,
          tension: 0.3
        },
        ...this.comparisonSeries.map((series, idx) => ({
          label: series.label || '',
          data: (series.data || []).map(item => Number(item.value)),
          backgroundColor: 'rgba(0, 0, 0, 0)',
          borderColor: comparisonColors[idx % comparisonColors.length],
          pointBackgroundColor: comparisonColors[idx % comparisonColors.length],
          borderWidth: 2,
          borderDash: [6, 4],
          pointRadius: 3,
          pointHoverRadius: 6,
          tension: 0.3
        }))
      ];
      this.chart = new Chart(ctx, {
        type: 'line',
        data: {
          labels: this.labels,
          datasets: datasets
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: { duration: 500, easing: 'easeOutQuart' },
          tooltips: {
            callbacks: {
              label: (tooltipItem, data) => {
                const ds = data.datasets[tooltipItem.datasetIndex];
                const prefix = ds.label ? ds.label + ': ' : '';
                return prefix + this.getValueFormat(this.data_format, tooltipItem.value);
              }
            }
          },
          scales: {
            yAxes: [
              {
                ticks: {
                  beginAtZero: true,
                  fontColor: this.axisTextColor,
                  callback: value => this.getValueFormat(this.data_format, value),
                  stepSize: 10
                },
                gridLines: {
                  color: this.gridLineColor,
                  drawBorder: true,
                  drawTicks: true,
                  zeroLineColor: this.gridLineColor
                }
              }
            ],
            xAxes: [
              {
                ticks: {
                  fontColor: this.axisTextColor
                },
                gridLines: {
                  color: this.gridLineColor,
                  drawBorder: true,
                  drawTicks: true
                }
              }
            ]
          },
          legend: {
            display: this.comparisonSeries.length > 0,
            labels: { fontColor: this.axisTextColor, usePointStyle: false }
          },
          layout: {
            padding: {
              top: 20
            }
          }
        }
      });
    }
  },
  watch: {
    chartData: {
      deep: true,
      handler() {
        this.scheduleCreateChart();
      }
    },
    comparisonSeries: {
      deep: true,
      handler() {
        this.scheduleCreateChart();
      }
    },
    granularity() {
      this.scheduleCreateChart();
    }
  },
  beforeDestroy() {
    window.removeEventListener('dashboard-widget-resize', this.onTileResize);
    if (this.chart) {
      this.chart.destroy();
    }
  }
};
</script>

<style scoped>
canvas {
  height: 400px;
  display: block;
  margin-bottom: 40px;
  padding-bottom: 10px;
  transition: opacity 0.35s ease;
}
canvas.chart-canvas-fill {
  height: 100%;
  margin-bottom: 0;
  padding-bottom: 0;
}
.chart-hidden {
  opacity: 0;
}
.chart-loading {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
.chart-spin {
  font-size: 1.5rem;
  color: rgba(125, 125, 125, 0.8);
  animation: chart-spin 1s linear infinite;
}
@keyframes chart-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
